import React, { useEffect } from 'react'
import { AgreementViewPageProps } from './AgreementViewPage.types'
import styles from './AgreementViewPage.module.scss'
import cl from 'classnames'
import { AGREEMENT_PAGE_STORE } from 'store'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { Button, ButtonProps, CurrencySelector, H2, ImagePortrait, Numbers, PageHeader, PrintButton, SectionContainer, Text1, VerticalLine } from 'components/shared'
import { IconAgreements, IconCalendar, IconDeleteCircle } from 'assets'
import { capitalize } from 'validate.js'
import { AgreementItemPropertyTypes, AgreementItemTypes, AgreementStatusTypes, getAgreementStatusLabel } from 'models'
import { formatPrice, getMainImageSrcForObj, useGetIsResponsiveScreen } from 'utilities'
import { ScopeProductsSection } from './ScopeProductsSection'
import { AgreementPageHeaderSecondLine } from '../AgreementPageHeaderSecondLine'
import { RedMarkedText } from 'components/shared/RedMarkedText'

const AgreementViewPageComponent = (props: AgreementViewPageProps) => {
  const {
    className,
    style,
    [AGREEMENT_PAGE_STORE]: {
      getAgreementById,
      agreement,
      navLabel,
      agreementStatus,
      periodString,
      getItemProp,
      scopeBrand,
      scopeProjects,
      scopeProductsCount,
      scopeProjectsCount,
      getChangeByKeyId,
      hasVersionChanges,
      onAcceptAllChangesClick,
      onConfirmAgreementClick,
      isAgreementPendingApproval
    },
    history: { push },
    match: { params: { id } }
  } = props

  const { isResponsive: isLargeMobile } = useGetIsResponsiveScreen('(max-width: 920px)')

  useEffect(() => getAgreementById(id), [getAgreementById, id])

  const getHeaderButtonProps = (): ButtonProps[] => {
    const buttonProps: ButtonProps[] = []

    const buttons: { [key: string]: ButtonProps } = {
      rejectAll: {
        children: 'Reject all',
        preset: 'secondary',
        disabled: true
      },
      edit: {
        children: 'Edit',
        preset: 'primary',
        onClick: () => push(`/agreements/edit/${id}`)
      },
      editSecondary: {
        children: 'Edit',
        preset: 'secondary',
        onClick: () => push(`/agreements/edit/${id}`)
      },
      acceptAll: {
        children: 'Accept all',
        preset: 'primary',
        onClick: onAcceptAllChangesClick
      },
      confirm: {
        children: 'Confirm agreement',
        preset: 'primary',
        onClick: onConfirmAgreementClick
      }
    }

    switch (true) {
      case isAgreementPendingApproval: {
        if (hasVersionChanges) {
          buttonProps.push(
            buttons.rejectAll,
            buttons.edit,
            buttons.acceptAll
          )
        } else {
          buttonProps.push(
            buttons.editSecondary,
            buttons.confirm
          )
        }
        break
      }
      case ([
        AgreementStatusTypes.PENDING_PROJECT_APPROVAL,
        AgreementStatusTypes.PENDING_STORE_APPROVAL,
        AgreementStatusTypes.DRAFT_PROJECT,
        AgreementStatusTypes.DRAFT_STORE
      ].includes(agreementStatus)): {
        buttonProps.push(
          buttons.edit
        )
        break
      }
    }

    return buttonProps
  }

  return (
    <div
      className={cl(styles.pageContainer, styles.scrollable, className)}
      style={ { ...style, overflowX: 'hidden' }}
    >
      <PageHeader
        navSegments={[
          {
            label: 'Agreements',
            icon: IconAgreements,
            onClick: () => push('/agreements')
          },
          {
            label: navLabel
          }
        ]}
        secondLineContainerProps={{
          children: <AgreementPageHeaderSecondLine />
        }}
        buttonsContainerChildren={<>
          <CurrencySelector />
          <VerticalLine style={{ margin: '0 0 0 30px' }} />
          {getHeaderButtonProps().map((buttonProps, index) => {
            return (
              <Button key={index} {...buttonProps} />
            )
          })}
          <PrintButton style={{ marginLeft: 25 }} />
        </>}
      />

      {/* Agreement details */}
      <SectionContainer className={styles.pageSection} extraPaddingBottom>
        <H2 style={ { marginBottom: 10 } }>Agreement details</H2>

        <div className={styles.agreementStatusContainer}>
          <div>
            <Text1 bold>Status:</Text1>
            <Text1 style={{ color: styles.primary }}>
              {capitalize(getAgreementStatusLabel(agreementStatus))}
            </Text1>
          </div>
          <div>
            <Text1 bold>Currently affecting a published project:</Text1>
            <Text1 style={{ color: styles.primary }}>
              {agreement?.effectOnElements ? 'Yes' : 'No'}
            </Text1>
          </div>
          <div>
            <Text1 bold>Remaining budget</Text1>
            <div className={styles.budgetNumbersContainer}>
              <Numbers style={{ color: styles.primary }}>
                {formatPrice(agreement?.budgetRemaining)}
              </Numbers>
              <Text1 style={{ color: styles.placeholderContent, marginBottom: -2 }}>
                / {formatPrice(agreement?.budget)}
              </Text1>
            </div>
          </div>
          {[
            AgreementStatusTypes.AGREED,
            AgreementStatusTypes.NOT_ACTIVE,
            AgreementStatusTypes.NEW
          ].includes(agreementStatus) && (
            <Button
              preset='secondary'
              onClick={() => push(`/agreements/edit/${agreement?.id}`)}
            >
                Edit details
            </Button>
          )}
        </div>

        <SectionContainer preset='secondary' style={{ marginTop: 30 }}>
          <div className={styles.labeledValueContainer}>
            <Text1
              bold
              versionChangeAsLabel={getChangeByKeyId('name')}
            >
              Name
            </Text1>
            <Text1
              className={styles.propValue}
              versionChange={getChangeByKeyId('name')}
            >
              {agreement?.name}
            </Text1>
          </div>
          <div className={styles.sectionLine}>
            <div className={styles.labeledValueContainer}>
              <Text1
                bold
                versionChangeAsLabel={getChangeByKeyId('budget')}
              >
                Budget
              </Text1>
              <Text1
                className={styles.propValue}
                versionChange={getChangeByKeyId('budget')}
              >
                {formatPrice(agreement?.budget)}
              </Text1>
            </div>
            <div className={styles.labeledValueContainer}>
              <Text1
                bold
                versionChangeAsLabel={getChangeByKeyId('budgetBuffer')}
              >
                Buffer
              </Text1>
              <Text1
                className={styles.propValue}
                versionChange={getChangeByKeyId('budgetBuffer')}
              >
                {agreement?.budgetBuffer}%
              </Text1>
            </div>
            <div className={styles.labeledValueContainer}>
              <Text1
                bold
                versionChangeAsLabel={getChangeByKeyId('period')}
              >
                Period
              </Text1>
              <Text1
                className={cl(styles.propValue, {
                  [styles.red]: getChangeByKeyId('period')
                })}
              >
                <IconCalendar style={{ marginRight: 10 }} />
                {getChangeByKeyId('period')?.valueBeforeChange && (
                  <RedMarkedText className={styles.redMarkedText}>
                    {getChangeByKeyId('period').valueBeforeChange}
                  </RedMarkedText>
                )}
                {periodString}
              </Text1>
            </div>
          </div>
        </SectionContainer>
      </SectionContainer>

      {/* Configuration */}
      <SectionContainer className={styles.pageSection} extraPaddingBottom>
        <H2>Configuration</H2>
        <SectionContainer preset='secondary' className={styles.pageSection}>
          <Text1 bold>BLOB</Text1>
          <div className={cl(styles.flexContainer, styles.marginTop)}>
            <div className={styles.flexContainer}>
              <div className={styles.labeledValueContainer}>
                <Text1
                  bold
                  versionChangeAsLabel={getChangeByKeyId(AgreementItemPropertyTypes.BLOB_TRIGGERED)}
                >
                  Price per one trigger
                </Text1>
                <Text1
                  className={styles.propValue}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.BLOB_TRIGGERED)}
                >
                  ${
                    getItemProp(
                      AgreementItemTypes.BLOB_SETTINGS,
                      AgreementItemPropertyTypes.BLOB_TRIGGERED
                    )
                  }
                </Text1>
              </div>
              <div className={styles.labeledValueContainer}>
                <Text1
                  bold
                  versionChangeAsLabel={getChangeByKeyId(AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS)}
                >
                  Max attach time
                </Text1>
                <Text1
                  className={styles.propValue}
                  versionChange={getChangeByKeyId(AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS)}
                >
                  {getItemProp(
                    AgreementItemTypes.BLOB_SETTINGS,
                    AgreementItemPropertyTypes.MAX_ATTACH_TIME_SECONDS
                  )} seconds
                </Text1>
              </div>
            </div>

            <div>
              <Text1 bold style={{ marginBottom: 10 }}>
                Price per time on screen
              </Text1>
              <div className={styles.flexContainer}>
                <div className={styles.flexContainer}>
                  <div className={styles.labeledValueContainer}>
                    <Text1
                      className={styles.secondaryLabel}
                      versionChangeAsLabel={getChangeByKeyId(
                        AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED
                      )}
                    >
                      Attached
                    </Text1>
                  </div>
                  <Text1
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED)}
                  >
                    ${
                      getItemProp(
                        AgreementItemTypes.BLOB_SETTINGS,
                        AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_ATTACHED
                      )
                    }
                  </Text1>
                </div>
                <div className={styles.flexContainer}>
                  <div className={styles.labeledValueContainer}>
                    <Text1
                      className={styles.secondaryLabel}
                      versionChangeAsLabel={getChangeByKeyId(
                        AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED
                      )}
                    >
                      Detached
                    </Text1>
                  </div>
                  <Text1
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED)}
                  >
                    ${
                      getItemProp(
                        AgreementItemTypes.BLOB_SETTINGS,
                        AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN_DETACHED
                      )
                    }
                  </Text1>
                </div>
              </div>
            </div>

            <div>
              <Text1 bold style={{ marginBottom: 10 }}>
                Time in stack
              </Text1>
              <div className={styles.flexContainer}>
                <div className={styles.flexContainer}>
                  <div className={styles.labeledValueContainer}>
                    <Text1
                      className={styles.secondaryLabel}
                      versionChangeAsLabel={getChangeByKeyId(
                        AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK
                      )}
                    >
                      Min
                    </Text1>
                  </div>
                  <Text1
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK)}
                  >{
                      getItemProp(
                        AgreementItemTypes.BLOB_SETTINGS,
                        AgreementItemPropertyTypes.MINIMUM_TIME_IN_STACK
                      )
                    } seconds
                  </Text1>
                </div>
                <div className={styles.flexContainer}>
                  <div className={styles.labeledValueContainer}>
                    <Text1
                      className={styles.secondaryLabel}
                      versionChangeAsLabel={getChangeByKeyId(
                        AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK
                      )}
                    >
                      Max
                    </Text1>
                  </div>
                  <Text1
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK)}
                  >{
                      getItemProp(
                        AgreementItemTypes.BLOB_SETTINGS,
                        AgreementItemPropertyTypes.MAXIMUM_TIME_IN_STACK
                      )
                    } seconds
                  </Text1>
                </div>
              </div>
            </div>
          </div>
        </SectionContainer>

        <div className={styles.flexContainer}>
          <SectionContainer preset='secondary' className={styles.pageSection} style={{ flex: 2 }}>
            <Text1 bold>PRODUCT</Text1>
            <div className={cl(styles.flexContainer, styles.marginTop)}>
              <div className={styles.flexContainer}>
                <div className={styles.labeledValueContainer}>
                  <Text1
                    bold
                    versionChangeAsLabel={getChangeByKeyId(
                      AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB
                    )}
                  >
                    Price per opened product page
                  </Text1>
                  <Text1
                    className={styles.propValue}
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB)}
                  >
                    ${
                      getItemProp(
                        AgreementItemTypes.PRODUCT,
                        AgreementItemPropertyTypes.PRODUCT_PAGE_OPENED_FROM_BLOB
                      )
                    }
                  </Text1>
                </div>
                <div className={styles.labeledValueContainer}>
                  <Text1
                    bold
                    versionChangeAsLabel={getChangeByKeyId(
                      AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART
                    )}
                  >
                    Price per product added to cart
                  </Text1>
                  <Text1
                    className={styles.propValue}
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART)}
                  >
                    ${
                      getItemProp(
                        AgreementItemTypes.PRODUCT,
                        AgreementItemPropertyTypes.PRODUCT_ADDED_TO_CART
                      )
                    }
                  </Text1>
                </div>
              </div>
            </div>
          </SectionContainer>
          <SectionContainer preset='secondary' className={styles.pageSection}>
            <Text1 bold>VIDEO COMMERCIAL</Text1>
            <div className={cl(styles.flexContainer, styles.marginTop)}>
              <div className={styles.flexContainer}>
                <div className={styles.labeledValueContainer}>
                  <Text1
                    bold
                    versionChangeAsLabel={getChangeByKeyId(
                      AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK
                    )}
                  >
                    Price per video playback
                  </Text1>
                  <Text1
                    className={styles.propValue}
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK)}
                  >
                    ${
                      getItemProp(
                        AgreementItemTypes.VIDEO_COMMERCIAL,
                        AgreementItemPropertyTypes.PRICE_PER_VIDEO_PLAYBACK
                      )
                    }
                  </Text1>
                </div>
              </div>
            </div>
          </SectionContainer>
        </div>

        <div className={styles.flexContainer}>
          <SectionContainer preset='secondary' className={styles.pageSection} style={{ flex: 2 }}>
            <Text1 bold>POSTER</Text1>
            <div className={cl(styles.flexContainer, styles.marginTop)}>
              <div className={styles.flexContainer}>
                <div className={styles.labeledValueContainer}>
                  <Text1
                    bold
                    versionChangeAsLabel={getChangeByKeyId(
                      AgreementItemPropertyTypes.PRICE_PER_CLICK
                    )}
                  >
                    Price per click
                  </Text1>
                  <Text1
                    className={styles.propValue}
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_CLICK)}
                  >
                    ${
                      getItemProp(
                        AgreementItemTypes.POSTER,
                        AgreementItemPropertyTypes.PRICE_PER_CLICK
                      )
                    }
                  </Text1>
                </div>
                <div className={styles.labeledValueContainer}>
                  <Text1
                    bold
                    versionChangeAsLabel={getChangeByKeyId(
                      AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN
                    )}
                  >
                    Price per second on screen
                  </Text1>
                  <Text1
                    className={styles.propValue}
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN)}
                  >
                    ${
                      getItemProp(
                        AgreementItemTypes.POSTER,
                        AgreementItemPropertyTypes.PRICE_PER_TIME_ON_SCREEN
                      )
                    }
                  </Text1>
                </div>
              </div>
            </div>
          </SectionContainer>
          <SectionContainer preset='secondary' className={styles.pageSection}>
            <Text1 bold>PRODUCT PURCHASE</Text1>
            <div className={cl(styles.flexContainer, styles.marginTop)}>
              <div className={styles.flexContainer}>
                <div className={styles.labeledValueContainer}>
                  <Text1
                    bold
                    versionChangeAsLabel={getChangeByKeyId(
                      AgreementItemPropertyTypes.PURCHASE_FEE
                    )}
                  >
                    Purchase fee
                  </Text1>
                  <Text1
                    className={styles.propValue}
                    versionChange={getChangeByKeyId(AgreementItemPropertyTypes.PURCHASE_FEE)}
                  >
                    {getItemProp(
                      AgreementItemTypes.PRODUCT_PURCHASE,
                      AgreementItemPropertyTypes.PURCHASE_FEE
                    )}%
                  </Text1>
                </div>
              </div>
            </div>
          </SectionContainer>
        </div>
      </SectionContainer>

      {/* Agreement scope */}
      <SectionContainer className={styles.pageSection} extraPaddingBottom>
        <div className={cl((isLargeMobile ? styles.flexCol : styles.flexRow), styles.gap10) }>
          <H2>Agreement scope</H2>
          <Text1 className={styles.agreementScopeInfo} overflowHidden={false}>
            {scopeProductsCount} products, {scopeProjectsCount} projects
          </Text1>
        </div>
        <SectionContainer preset='secondary' className={styles.pageSection}>
          <Text1>This agreement is valid for:</Text1>
          <div className={cl(styles.flexContainer, styles.marginTop)} style={{ paddingRight: 70 }}>
            <div className={styles.labeledValueContainer}>
              <Text1
                bold
                versionChangeAsLabel={getChangeByKeyId('store')}
              >
                Store
              </Text1>
              <div style={{ display: 'flex', marginTop: 10 }}>
                <ImagePortrait
                  src={getMainImageSrcForObj(scopeBrand)}
                  style={{ marginRight: 10 }}
                />
                <Text1 versionChange={getChangeByKeyId('store')}>
                  {scopeBrand?.name}
                </Text1>
              </div>
            </div>
            <div style={{ flex: 2 }}>
              <Text1 bold>Projects</Text1>
              <div className={styles.flexWrap}>
                {scopeProjects.map(prj => {
                  return (
                    <div key={prj.id} className={styles.labeledValueContainer}>
                      <Text1
                        className={styles.propValue}
                        overflowEllipsis
                        title={prj?.name}
                        versionChangeAsLabel={getChangeByKeyId(`project-${prj.id}`)}
                        versionChange={getChangeByKeyId(`project-${prj.id}`)}
                      >
                        {prj?.name}
                      </Text1>
                    </div>
                  )
                })}

              </div>
            </div>
          </div>
          <ScopeProductsSection />
        </SectionContainer>
      </SectionContainer>

      <Button
        preset='icon'
        className={styles.cancelButton}
        // disabled
      >
        <IconDeleteCircle />
        Cancel agreement
      </Button>
    </div>
  )
}

const AgreementViewPage = inject(
  AGREEMENT_PAGE_STORE
)(observer(AgreementViewPageComponent))
export { AgreementViewPage }
